import { Grid, Box } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { string, object } from "yup";
import ImageUpload from "src/components/ImageUpload";
import { useContext } from "react";
import { AppContext } from "src/api/AppContext";
import { useUpdateUserMutation } from "src/generated/graphql";
import { useDisclosure } from "@mantine/hooks";
import TextInput from "src/components/TextInput/TextInput";
import NumberInput from "src/components/NumberInput/NumberInput";
import Switch from "src/components/Switch/Switch";
import { MESSAGES } from "src/utils/constants";
import FormCard from "src/components/FormCard";

interface IProfileFormPayload {
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: number;
  profilePicture: string | ArrayBuffer;
  smsConsent: boolean;
  staffId?: string;
  pin?: string;
  username?: string;
}

const profileFormSchema = object().shape({
  staffId: string()
    .nullable()
    .matches(/^\S*$/, "must not contain spaces")
    .max(20, "Staff ID must not be greater than 20 letters"),
  firstName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "First Name must be at least 2 letters")
    .required("First Name is required")
    .max(30, "First Name must not be greater than 30 letters"),
  middleName: string()
    .trim("No spaces allowed")
    .strict(true)
    .matches(/.{2,}/, {
      excludeEmptyString: true,
      message: "Middle Name must be at least 2 characters",
    })
    .max(30, "Middle Name must not be greater than 30 letters"),
  lastName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Last Name must be at least 2 letters")
    .required("Last Name is required")
    .max(30, "Last Name must not be greater than 30 letters"),
  phoneNumber: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
});

export const ProfileForm = () => {
  const { currentUser, refetchUserData, currentEmployee } =
    useContext(AppContext);
  console.log(currentUser, currentEmployee);
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const form = useForm({
    initialValues: {
      email: currentUser?.email,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      middleName: currentUser?.middleName,
      phoneNumber: currentUser?.phoneNumber
        ? Number(currentUser?.phoneNumber)
        : undefined,
      profilePicture: currentUser?.profilePicUrl,
      smsConsent: currentUser?.smsConsent,
      staffId: currentEmployee?.staffId,
      pin: currentEmployee?.pin,
      username: currentEmployee?.user.username,
    } as IProfileFormPayload,
    validate: yupResolver(profileFormSchema),
  });

  const [updateProfile, { loading }] = useUpdateUserMutation({
    onCompleted: () => {
      refetchUserData();
      toggleFormActive();
      form.resetDirty();
    },
  });

  const handleProfileUpdate = async (payload: IProfileFormPayload) => {
    try {
      const {
        profilePicture,
        phoneNumber,
        staffId,
        pin,
        username,
        ...restPayload
      } = payload;
      updateProfile({
        variables: {
          data: {
            profilePic: profilePicture as string,
            phoneNumber: phoneNumber ? `+${phoneNumber}` : undefined,
            ...(currentEmployee && { staffId, pin }),
            ...restPayload,
          },
          id: currentUser?.id as string,
        },
      });
    } catch (e: any) {
      console.error("Error: ", e.response);
    }
  };

  const setImageFile = (file: string | ArrayBuffer) => {
    form.setFieldValue("profilePicture", file); // by using base64
  };

  // form field props
  const { value: imageSrcValue, ...profilePicProps } =
    form.getInputProps("profilePicture");

  const { value: smsConsentValue, ...smsConsentProps } =
    form.getInputProps("smsConsent");

  const { value: phoneNumberValue, ...phoneNumberProps } =
    form.getInputProps("phoneNumber");

  const resetFormState = () => {
    let _data = {
      email: currentUser?.email,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      middleName: currentUser?.middleName,
      phoneNumber: currentUser?.phoneNumber
        ? Number(currentUser?.phoneNumber)
        : undefined,
      profilePicture: currentUser?.profilePicUrl,
      smsConsent: currentUser?.smsConsent,
      staffId: currentEmployee?.staffId,
      pin: currentEmployee?.pin,
      username: currentEmployee?.user.username,
    } as IProfileFormPayload;
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (/^\d*$/.test(event.target.value)) {
      form.setFieldValue("pin", event.target.value);
    }
  };

  const isFormUpdated = form.isDirty() && form.isTouched();
  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={loading}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onSave={form.onSubmit((values) => handleProfileUpdate(values))}
        isFormActive={isFormActive}
      >
        <Box mx={18}>
          <Grid gutter={18} m={0}>
            <Grid.Col span={12} lg={5} mt="xl">
              <ImageUpload
                onReadSuccess={setImageFile}
                src={imageSrcValue}
                alt="Profile Image"
                disabled={isFormActive}
                {...profilePicProps}
              />
            </Grid.Col>
            <Grid.Col span={12} lg={7} mt="md">
              {currentEmployee && (
                <>
                  <TextInput
                    label="Staff ID"
                    disabled={isFormActive}
                    {...form.getInputProps("staffId")}
                  />
                  <TextInput
                    label="Pin"
                    disabled={isFormActive}
                    maxLength={4}
                    {...form.getInputProps("pin")}
                    onChange={handlePinChange}
                  />
                  <TextInput
                    label="Username"
                    disabled
                    {...form.getInputProps("username")}
                  />
                </>
              )}
              <TextInput
                withAsterisk
                label="Email"
                disabled
                {...form.getInputProps("email")}
              />
              <TextInput
                withAsterisk
                label="First Name"
                disabled={isFormActive}
                {...form.getInputProps("firstName")}
              />
              <TextInput
                label="Middle Name"
                disabled={isFormActive}
                {...form.getInputProps("middleName")}
              />
              <TextInput
                withAsterisk
                label="Last Name"
                disabled={isFormActive}
                {...form.getInputProps("lastName")}
              />
              <NumberInput
                label="Phone Number"
                disabled={isFormActive}
                value={phoneNumberValue}
                {...phoneNumberProps}
              />
              <Switch
                mt="lg"
                label="Do you consent to receiving text messages to the provided phone number?"
                checked={smsConsentValue}
                disabled={isFormActive}
                {...smsConsentProps}
              />
            </Grid.Col>
          </Grid>
        </Box>
      </FormCard>
      {/* <SectionCard title="Settings" mt={16}>
        <Text ta="center">Placeholder for additional user settings</Text>
      </SectionCard> */}
    </>
  );
};
